import { assign, createMachine } from 'xstate';
import { billingMachine, billingContext, BillingDoneData } from '~/machines/billing-selection';
import { docuSignContext, docuSignMachine } from '~/machines/docusign';
import {
  enrollmentMachine,
  enrollmentContext,
  EnrollmentDoneData,
} from '~/machines/enrollment-selection';
import {
  legalMachine,
  legalContext,
  LegalDoneData,
  LegalContext,
} from '~/machines/legal-completion';
import { isFunnelTrackWaitlist } from '~/machines/solar-funnel';
import { docuSignUseCase, needsDocuSign, usesIpaContracts } from '~/machines/utils/docusign';
import { LDClient, LDFlags } from '~/providers';
import { ParsedQueryParams } from '~/providers/query-params';
import { createProductLead, fetchPlanTemplate, redirectToDashboard } from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchPlanTemplateResponse } from '~/services/fetch-plan-template';
import { BillingDataSource, EnrollmentDataSource } from '~/typings/context';
import { getInitialState } from '~/utils/funnels';
import { getEnrollmentOptions } from '~/utils/get-enrollment-options';
import { BILL_CAPTURE, config, PARTNER_PROVIDED } from './config';

export const funnelMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as Context,
        services: {} as Services,
      },
      context: {
        queryParams: {},
      },
      id: config.id,
      initial: getInitialState(config.id, ['enrollment', 'billing', 'legal']) || 'loading',
      entry: 'setFunnelPacket',
      states: {
        loading: {
          invoke: {
            id: 'fetchInitialData',
            src: 'fetchInitialData',
            onDone: [
              { cond: 'isUcbEnrollmentComplete', target: 'legal' },
              { cond: 'canSkipEnrollment', target: 'billing', actions: 'setInitialData' },
              { target: 'enrollment', actions: 'setInitialData' },
            ],
            onError: '#unexpected',
          },
        },
        enrollment: {
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              funnelPacket: funnelContext.funnelPacket!,
              enrollmentOptions: funnelContext.enrollmentOptions,
              startAtAccountNumber: funnelContext.queryParams.enrollmentOption === PARTNER_PROVIDED,
              startAtBillUpload: funnelContext.queryParams.enrollmentOption === BILL_CAPTURE,
              promoCode:
                funnelContext.queryParams.promoCode || funnelContext.funnelPacket?.user.promoCode,
              queryParams: funnelContext.queryParams,
            })),
            onDone: [{ cond: 'isUcb', target: 'legal' }, { target: 'billing' }],
          },
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              billingOptions: funnelContext.billingExperienceOptions,
              funnelPacket: funnelContext.funnelPacket!,
            })),
            onDone: { target: 'legal', actions: 'setPaymentMethod' },
          },
        },
        legal: {
          invoke: {
            id: 'legalMachine',
            src: 'legalMachine',
            data: legalContext(funnelContext => ({
              billingSelection: funnelContext.billingSelection!,
              funnelPacket: funnelContext.funnelPacket!,
              paymentMethod: funnelContext.paymentMethod,
            })),
            onDone: { target: 'completed', actions: 'setLegalSelection' },
          },
        },
        docuSign: {
          id: 'docuSign',
          invoke: {
            id: 'docuSignMachine',
            src: 'docuSignMachine',
            data: docuSignContext(funnelContext => ({
              contracts: funnelContext.docuSignContracts ?? [],
              straightToTimeout:
                usesIpaContracts(funnelContext) && !LDClient.variation(LDFlags.embeddedSigningIpa),
              useCase: docuSignUseCase(funnelContext),
              userId: funnelContext.funnelPacket!.user.id,
              utilityAccountId: funnelContext.funnelPacket!.utilityAccount.id,
              signedStatus: funnelContext.queryParams.docusignUser?.signedStatus,
            })),
            onDone: { target: 'completed.redirecting' },
          },
        },
        completed: {
          id: 'completed',
          initial: 'assigning',
          states: {
            assigning: {
              id: 'assigning',
              invoke: {
                id: 'createLeads',
                src: 'createLeads',
                onDone: [{ cond: 'needsDocuSign', target: '#docuSign' }, { target: 'redirecting' }],
                onError: '#unexpected',
              },
            },
            redirecting: {
              id: 'redirecting',
              type: 'final',
              onEntry: 'redirectToDashboard',
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
      },
    },
    {
      actions: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
        redirectToDashboard: context => {
          const isWaitlist = isFunnelTrackWaitlist(context);
          const waitlistPath = '/dashboard';
          const params = {
            userId: context.funnelPacket!.user.id,
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            billingExperienceType: context.billingSelection,
            ...(isWaitlist ? { path: waitlistPath } : {}),
          };
          redirectToDashboard(params);
        },
        setFunnelPacket: assign(context => ({
          funnelPacket: context.queryParams.resumingUser?.packet || context.funnelPacket,
        })),
        setInitialData: assign((_context, event) => ({
          billingSelection: event.data.billingExperienceOptions[0],
          billingExperienceOptions: event.data.billingExperienceOptions,
          enrollmentOptions: event.data.enrollmentOptions,
          enrollmentSelection: event.data.enrollmentOptions[0],
          products: event.data.products,
        })),
        setPaymentMethod: assign((_context, event) => ({
          paymentMethod: event.data.selectedPaymentMethod,
        })),
        setLegalSelection: assign((_context, event) => ({
          docuSignContracts: event.data.docuSignContracts,
        })),
        /* eslint-disable @typescript-eslint/no-unused-vars */
      },
      guards: {
        // Guards for various contexts
        isUcb: context => context.billingSelection === 'ucb',
        // Guard to check if user can skip enrollment step in the funnel.
        canSkipEnrollment: (context, event) =>
          event.data.enrollmentOptions.includes('utility_connect') &&
          context.funnelPacket?.utilityCredential.verificationStatus === 'correct',
        // UCB users must not be asked for billing details
        isUcbEnrollmentComplete: (context, event) =>
          isUcb(context) && skipsEnrollment(context, event),

        needsDocuSign,
      },
      services: {
        billingMachine: billingMachine(),
        enrollmentMachine: enrollmentMachine(),
        legalMachine: legalMachine(),
        docuSignMachine: docuSignMachine(),
        // manually create product leads rather than overwriting everything via the signupUser service for now
        createLeads: async context => {
          const planId = context.funnelPacket!.customPlan.id;
          const utilityAccountId = context.funnelPacket!.utilityAccount.id;
          const billPayProduct = context.products?.find(p => p.productType === 'BillPayService');
          const vnmProduct = context.products?.find(p => p.productType === 'VNM');
          if (billPayProduct) {
            await createProductLead({ planId, productId: billPayProduct.id, utilityAccountId });
          }
          if (vnmProduct) {
            await createProductLead({ planId, productId: vnmProduct.id, utilityAccountId });
          }
          return undefined;
        },
        // exchange plan template for datasources
        fetchInitialData: async context => {
          const { billingExperienceOptions, products } = await fetchPlanTemplate({
            id: context.funnelPacket!.planTemplate!.id!,
          });
          // Fetch enrollment options from LD instead of using plan template
          const { enrollmentOptions } = getEnrollmentOptions({
            utilityId: context.funnelPacket!.utility.id,
          });
          return { billingExperienceOptions, enrollmentOptions, products };
        },
      },
    },
  );

type Context = {
  billingExperienceOptions?: BillingDataSource[];
  billingSelection?: BillingDataSource;
  enrollmentOptions?: EnrollmentDataSource[];
  enrollmentSelection?: EnrollmentDataSource;
  funnelPacket?: FetchFunnelPacketResponse;
  paymentMethod?: BillingDoneData['selectedPaymentMethod'];
  products?: { id: number; productType: 'VNM' | 'BillPayService' | string }[];
  queryParams: ParsedQueryParams;
  docuSignContracts?: LegalContext['docuSignContracts'];
};

type Services = {
  createLeads: { data: undefined };
  fetchInitialData: { data: FetchPlanTemplateResponse };
  billingMachine: { data: BillingDoneData };
  enrollmentMachine: { data: EnrollmentDoneData };
  legalMachine: { data: LegalDoneData };
};

const isUcb = context => context.funnelPacket?.planTemplate.billingExperienceType === 'ucb';
const skipsEnrollment = (context, event) =>
  event.data.enrollmentOptions.includes('utility_connect') &&
  context.funnelPacket?.utilityCredential.verificationStatus === 'correct';
